import { inject, Injectable, Injector } from '@angular/core'
import { ActivationEnd, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { EMPTY, merge, of } from 'rxjs'
import {
  tap,
  distinctUntilChanged,
  filter,
  switchMap,
  map,
  catchError,
  exhaustMap,
  take,
  timeout
} from 'rxjs/operators'
import * as fromActions from './auth.actions'

import {
  TitleService,
  AppState,
  selectSettingsLanguage,
  NotificationService
} from '../../../core/core.module'
import { actionSettingsChangeLanguage } from '../../../core/settings/settings.actions'
import { OrganizationMemberService } from '../../organization/services/organization-member.service'
import { jwtDecode, JwtPayload } from 'jwt-decode'

export interface CustomJwtPayload extends JwtPayload {
  email?: string
  name?: string
  username?: string
  preferred_username?: string // Opcional si el backend lo usa
}

@Injectable()
export class AuthenticationEffects {
  private actions$ = inject(Actions)
  private store = inject(Store<AppState>)
  private translateService = inject(TranslateService)
  private organizationMemberService = inject(OrganizationMemberService)
  private notificationService = inject(NotificationService)
  private router = inject(Router)
  private titleService = inject(TitleService)

  setTranslateServiceLanguage = createEffect(
    () =>
      this.store.pipe(
        select(selectSettingsLanguage),
        distinctUntilChanged(),
        tap((language) => this.translateService.use(language))
      ),
    { dispatch: false }
  )

  setTitle = createEffect(
    () =>
      // eslint-disable-next-line ngrx/prefer-effect-callback-in-block-statement
      merge(
        this.actions$.pipe(ofType(actionSettingsChangeLanguage)),
        this.router.events.pipe(
          filter((event) => event instanceof ActivationEnd)
        )
      ).pipe(
        tap(() => {
          this.titleService.setTitle(
            this.router.routerState.snapshot.root,
            this.translateService
          )
        })
      ),
    { dispatch: false }
  )

  sendVerificationTicket$ = createEffect(
    () =>
      // eslint-disable-next-line ngrx/prefer-effect-callback-in-block-statement
      this.actions$.pipe(
        ofType(fromActions.resendVerificationTicket),
        exhaustMap((action) => {
          const { email } = action
          return this.organizationMemberService
            .sendVerificationTicket(email)
            .pipe(
              map((response) =>
                fromActions.successResendInviteOrganizationMember({ response })
              ),
              catchError((error) => of(fromActions.error({ error })))
            )
        })
      ),
    { dispatch: true }
  )

  sendVerificationEmail$ = createEffect(
    () =>
      // eslint-disable-next-line ngrx/prefer-effect-callback-in-block-statement
      this.actions$.pipe(
        ofType(fromActions.sendVerificationEmail),
        exhaustMap((action) => {
          const { email } = action
          return this.organizationMemberService
            .sendVerificationEmail(email)
            .pipe(
              map((response) =>
                fromActions.successResendInviteOrganizationMember({ response })
              ),
              catchError((error) => of(fromActions.error({ error })))
            )
        })
      ),
    { dispatch: true }
  )

  notifyResend = createEffect(
    // eslint-disable-next-line arrow-body-style
    () => {
      return this.actions$.pipe(
        ofType(fromActions.successResendInviteOrganizationMember),

        tap((actions) => {
          this.notificationService.info(
            this.translateService.instant(
              'anms.organization.member.email.resent.success'
            )
          )
        })
      )
    },
    { dispatch: false }
  )

  error$ = createEffect(
    // eslint-disable-next-line arrow-body-style
    () => {
      return this.actions$.pipe(
        ofType(fromActions.error),

        // eslint-disable-next-line arrow-body-style
        tap((action: any) => {
          const message: string = action.error.message

          this.notificationService.error(message)
        })
      )
    },
    { dispatch: false }
  )
}
